import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Input, Spinner } from "reactstrap";
import { placeOrder } from "../../api/orders";
import { apiErrorHandler } from "../../helpers/common";
import { useSelector } from "react-redux";

const AddressForm = ({ planId, setCheckoutRes }) => {
  const INITIAL_STATE = {
    name: "",
    city: "",
    pin_code: "",
    additional_mobile_number: "",
    address: "",
    country_code: "IN",
    gst_number: "",
  };
  const validateGST = (gstin) => {
    // If left empty, pass validation since it's optional
    if (!gstin || gstin.trim() === "") {
      return "";
    }

    const gstRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

    if (!gstRegex.test(gstin.trim().toUpperCase())) {
      return "Please enter a valid 15-digit GST Number (e.g. 22AAAAA0000A1Z5)";
    }

    return "";
  };
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});

  const employer = useSelector((state) => state.employer);
  const companies = employer.companies;

  useEffect(() => {
    if (companies?.length) {
      const company = companies[0];

      setFormData((prevData) => ({
        ...prevData,
        name: company?.name,
        additional_mobile_number:
          company?.contact_number || company?.mobile_number,
        address: company?.address,
        gst_number: company?.gst_number,
      }));
    }
  }, [companies]);

  const onValueChange = (e, name) => {
    setFormData({ ...formData, [name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      setErrors({ name: "Please enter your name" });
      return;
    }

    if (!formData.city) {
      setErrors({ city: "Please enter your city" });
      return;
    }

    if (!formData.address) {
      setErrors({ address: "Please enter your full address" });
      return;
    }

    if (!formData.pin_code) {
      setErrors({ pin_code: "Please enter your pin code" });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        planId: parseInt(planId),
        items: [],
        customer: {
          name: formData.name,
          city: formData.city,
          pinCode: formData.pin_code, // ✅ mapped
          additionalMobileNumber: formData.additional_mobile_number, // ✅ mapped
          address: formData.address,
          countryCode: formData.country_code,
          gstNumber: formData.gst_number, // ✅ mapped
          state: "", // ✅ added (empty if not in UI)
          country: "", // ✅ added (empty if not in UI)
        },
      };
      const response = await placeOrder(payload);
      setCheckoutRes(response?.data);
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const ErrorMsg = ({ msg }) => {
    return <small className="text-danger text-small mt-5">{msg}</small>;
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="mb-3">
        <label htmlFor="name" className="form-label">
          Company Name / Your Name *
        </label>
        <Input
          type="text"
          value={formData.name}
          className="form-control"
          id="name"
          placeholder="Enter name"
          name="name"
          invalid={errors.name ? true : false}
          onChange={(e) => onValueChange(e, "name")}
        />

        <ErrorMsg msg={errors.name} />
      </div>

      <div className="mb-3">
        <label htmlFor="additional_mobile_number" className="form-label">
          Phone number
        </label>
        <Input
          type="text"
          value={formData.additional_mobile_number || ""}
          className="form-control"
          id="additional_mobile_number"
          placeholder="Enter 10 digit phone number"
          name="additional_mobile_number"
          maxLength={10}
          invalid={errors.additional_mobile_number ? true : false}
          onChange={(e) => {
            e.target.value = e.target.value.replace(/\D/g, "").slice(0, 10);
            onValueChange(e, "additional_mobile_number");
          }}
        />
        <ErrorMsg msg={errors.additional_mobile_number} />
      </div>

      <div className="mb-3">
        <label htmlFor="city" className="form-label">
          City *
        </label>
        <Input
          type="text"
          value={formData.city}
          className="form-control"
          id="city"
          placeholder="Enter city"
          name="city"
          invalid={errors.city ? true : false}
          onChange={(e) => onValueChange(e, "city")}
        />
        <ErrorMsg msg={errors.city} />
      </div>

      <div className="mb-3">
        <label htmlFor="address" className="form-label">
          Your Address / Company Address *
        </label>
        <Input
          type="textarea"
          rows={3}
          value={formData.address}
          className="form-control"
          id="address"
          placeholder="Enter address here"
          name="address"
          invalid={errors.address ? true : false}
          onChange={(e) => onValueChange(e, "address")}
        />
        <ErrorMsg msg={errors.address} />
      </div>

      <div className="mb-3">
        <label htmlFor="pincode" className="form-label">
          Pincode *
        </label>
        <Input
          type="text"
          value={formData.pin_code || ""}
          className="form-control"
          id="pincode"
          placeholder="Enter 6 digit pincode"
          name="pin_code"
          maxLength={6}
          invalid={errors.pin_code ? true : false}
          onChange={(e) => {
            e.target.value = e.target.value.replace(/\D/g, "").slice(0, 6);
            onValueChange(e, "pin_code");
          }}
        />
        <ErrorMsg msg={errors.pin_code} />
      </div>

      <div className="mb-3">
        <label htmlFor="gst_number" className="form-label">
          Please provide your GST number to receive a valid tax invoice for your
          company (Optional)
        </label>
        <Input
          type="text"
          value={formData.gst_number || ""}
          className="form-control text-uppercase"
          id="gst_number"
          placeholder="22AAAAA0000A1Z5"
          name="gst_number"
          maxLength={15}
          invalid={!!errors.gst_number}
          onChange={(e) => {
            e.target.value = e.target.value.toUpperCase();
            onValueChange(e, "gst_number");
          }}
        />
        <ErrorMsg msg={errors.gst_number} />
      </div>

      {loading ? (
        <Link to="#" className="btn btn-primary disabled w-100">
          Submit <Spinner size="sm" />
        </Link>
      ) : (
        <>
          <button className="btn btn-primary w-100" type="submit">
            Submit <i className="mdi mdi-send"></i>
          </button>
        </>
      )}
    </form>
  );
};

export default AddressForm;
